import { useEffect, useRef, useState } from "react";

import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "react-query";

interface TQueryFnResponse<TQueryFnParams, TData, TError>
  extends Omit<UseQueryResult<TData, TError>, "refetch"> {
  refetch: (params: TQueryFnParams) => void;
}

/**
 * The useAsyncQuery is built based on the "useQuery" hook.
 * @param queryFn Must be an async function
 */
const useAsyncQuery = <
  TQueryFnParams = unknown,
  TQueryFnData = unknown,
  TData = Awaited<TQueryFnData>,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey
>(
  params: {
    queryFn: (params: TQueryFnParams) => TQueryFnData;
    initQueryParams?: TQueryFnParams;
    queryKey: TQueryKey;
  } & Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey" | "queryFn"
  >
): TQueryFnResponse<TQueryFnParams, TData, TError> => {
  const {
    initQueryParams: initState,
    enabled,
    queryKey,
    queryFn,
    ...innerParams
  } = params;

  const enabledFlag = useRef(enabled !== false);

  const [state, setState] = useState<TQueryFnParams | undefined>(initState);

  const { refetch: refetchQuery, ...innerQuery } = useQuery({
    enabled,
    queryKey,
    queryFn: () => queryFn?.(state as unknown as TQueryFnParams),
    ...innerParams,
  });

  useEffect(
    function refetchBasedOnChangedState() {
      if (!enabledFlag.current) return;
      refetchQuery();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  const refetchHanlder = (newState?: TQueryFnParams) => {
    enabledFlag.current = true;

    /**
     * When state is Array
     * When state is Object
     * When newState !== currentState (For primitive types)
     */
    if (
      Array.isArray(newState) ||
      (newState !== null && typeof newState === "object") ||
      newState !== state
    ) {
      setState(newState);
      return;
    }

    refetchQuery();
  };

  return {
    refetch: refetchHanlder,
    ...innerQuery,
  };
};

export default useAsyncQuery;
